import {useId, useState} from 'react'
import {FaAndroid, FaApple} from 'react-icons/fa6'
import {LuMonitor} from 'react-icons/lu'
import type {IconType} from 'react-icons'

import AngleDownIcon from '../../images/svg/AngleDownIcon'
import AngleRightIcon from '../../images/svg/AngleRightIcon'

interface AccordionStep {
  title: string
  body?: string
}

interface AccordionLink {
  text: string
  href: string
  target?: string
  rel?: string
}

interface Option {
  label: string
  value: string
  icon: IconType
  intro?: string
  steps?: AccordionStep[]
  links?: AccordionLink[]
  ctaLabel?: string
}

const options: Option[] = [
  {
    label: 'Android',
    value: 'android',
    icon: FaAndroid,
    intro:
      'On Android, nearby search may require location access in more than one place before this site can use your location.',
    steps: [
      {
        title: 'Turn on device location',
        body: 'Open your device Settings, go to Location, and make sure location services are turned on.',
      },
      {
        title: 'Allow location access for your browser app',
        body: 'In your device Settings, open Apps, select the browser you are using, open Permissions, and allow Location access.',
      },
      {
        title: 'Allow location access for this website',
        body: 'Open your browser settings or site settings and make sure this website is allowed to use your location.',
      },
      {
        title: 'Return to this screen and try nearby search again',
      },
    ],
    links: [
      {
        text: 'Learn more about enabling location permissions on Android',
        href: 'https://support.google.com/accounts/answer/3467281',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
    ctaLabel: 'Try Nearby Search Again',
  },
  {
    label: 'iPhone / iPad',
    value: 'ios',
    icon: FaApple,
    intro:
      'Nearby search requires Location Services to be enabled and your browser to be allowed to access location.',
    steps: [
      {
        title: 'Turn on Location Services',
        body: 'Open Settings, go to Privacy & Security, tap Location Services, and make sure it is turned on.',
      },
      {
        title: 'Allow location access for your browser',
        body: 'Check your browser and site settings to make sure location access is allowed for this website.',
      },
      {
        title: 'Return to this screen and try nearby search again',
      },
    ],
    links: [
      {
        text: 'Learn more about enabling location permissions on iOS',
        href: 'https://support.apple.com/en-us/102647',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
    ctaLabel: 'Try Nearby Search Again',
  },
  {
    label: 'Site Settings in Web Browsers',
    value: 'web_browsers',
    icon: LuMonitor,
    intro: 'Your browser may be preventing this website from accessing your location.',
    steps: [
      {
        title: 'Open browser settings',
      },
      {
        title: 'Go to Privacy and Security',
      },
      {
        title: 'Find site permissions or location settings',
      },
      {
        title: 'Ensure location access is enabled for this website',
      },
      {
        title: 'Return to this screen and try nearby search again',
      },
    ],
    ctaLabel: 'Try Nearby Search Again',
  },
]

export default function LocationPermissionsAccordion() {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const accordionId = useId()

  const handleOptionClick = (value: string) => {
    setSelectedOption((prevSelectedOption) => (prevSelectedOption === value ? null : value))
  }

  const renderPanelContent = (option: Option) => {
    return (
      <div className="pb-3 pt-1">
        {option.intro ? (
          <p className="mb-3 text-sm leading-1 text-slate-600 dark:text-slate-300">
            {option.intro}
          </p>
        ) : null}

        {option.steps?.length ? (
          <ol className="mb-4 space-y-1">
            {option.steps.map((step, index) => (
              <li
                key={`${option.value}-${index}`}
                className="text-sm text-slate-600 dark:text-slate-300"
              >
                <p
                  className={`leading-1 text-slate-800 dark:text-slate-100 ${option.value === 'web_browsers' ? 'font-medium' : 'font-semibold'}`}
                >
                  {index + 1}. {step.title}
                </p>
                {step.body ? <p className="mt-1 leading-1">{step.body}</p> : null}
              </li>
            ))}
          </ol>
        ) : null}

        {option.links?.length ? (
          <div className="mb-4 flex flex-col gap-2">
            {option.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target={link.target ?? '_blank'}
                rel={link.rel ?? 'noopener noreferrer'}
                className="text-sm font-medium text-sky-600 underline underline-offset-2 dark:text-sky-400"
              >
                {link.text}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-700 dark:bg-slate-900"
      aria-label="Location permissions help"
    >
      <div className="space-y-1">
        {options.map((option) => {
          const isExpanded = selectedOption === option.value
          const buttonId = `${accordionId}-button-${option.value}`
          const panelId = `${accordionId}-panel-${option.value}`

          return (
            <div
              key={option.value}
              className="border-b border-slate-200 last:border-b-0 dark:border-slate-700"
            >
              <h3>
                <button
                  id={buttonId}
                  type="button"
                  className={[
                    'location-permissions-accordion-button',
                    'flex w-full items-center justify-between gap-3 py-3 text-left',
                    'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
                  ].join(' ')}
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <span
                    className={`inline-flex items-center gap-2 text-sm ${
                      isExpanded
                        ? 'font-semibold text-slate-800 dark:text-slate-100'
                        : 'font-medium text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {/* @ts-ignore */}
                    <option.icon
                      className="h-4 w-4 shrink-0"
                      aria-hidden
                    />
                    {option.label}
                  </span>

                  <span
                    aria-hidden="true"
                    className="shrink-0"
                  >
                    {isExpanded ? (
                      <AngleDownIcon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    ) : (
                      <AngleRightIcon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    )}
                  </span>
                </button>
              </h3>

              {isExpanded ? (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="pr-2"
                >
                  {renderPanelContent(option)}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}
